import { isNil } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { SELECT_COLUMN_KEY } from "../Columns";
import { CellContextProps } from "../Row";
import { CalculatedColumn, ISelectRange, ISelectorEvent } from "../types";
import { isCtrlDown } from "../utils";
import { css } from '@linaria/core';
import { isCellInRange } from "../utils/selectRange";

const cellContext = css`
  position: absolute;
  z-index: 1;
  width: 150px;
  box-shadow: 0 3px 6px -4px rgb(0 0 0 / 12%), 0 6px 16px 0 rgb(0 0 0 / 8%), 0 9px 28px 8px rgb(0 0 0 / 5%);
`;

const defaultContextInfo = {
  x: 0,
  y: 0,
  columnIdx: 0,
  rowIdx: 0,
  column: null
};

interface IContextInfo<TRow, TSummaryRow> {
  x: number;
  y: number; 
  columnIdx: number;
  rowIdx: number;
  column: CalculatedColumn<TRow, TSummaryRow> | null
}

interface IProps<TRow, TSummaryRow> {
  viewportColumns: readonly CalculatedColumn<TRow, TSummaryRow>[];
  lazyStore: Map<string, any>;
  selectedRange: ISelectRange;
  selectedRows: Set<string>;
  selectedColumns: Set<string>;
  rows: TRow[];
  headerRowsCount: number;
  scrollTop: number;
  rowKeyName: string;
  rowKeyGetter: (row: {[key: string]: any; }) => string;
  onGridSelect: (data: ISelectorEvent) => void;
}

export default function useRowContext<TRow, TSummaryRow>({
  setShowMenuRowIdx,
  getContextMenuConfig,
  ContextMenuRender = () => null,
  onGridSelect,
  viewportColumns,
  lazyStore,
  selectedRange,
  selectedRows,
  selectedColumns,
  rows,
  rowKeyName,
  rowKeyGetter,
  scrollTop,
  headerRowsCount
}: CellContextProps & IProps<TRow, TSummaryRow>) {
  const [contextMenuVisible, setContextMenuVisible] = useState(false);
  const [contextInfo, setContextInfo] = useState<IContextInfo<TRow, TSummaryRow>>(defaultContextInfo);
  const contextRef = useRef<HTMLDivElement>(null);
  const { rowIdx, columnIdx } = contextInfo;
  const row = rows?.[rowIdx];
  const column = viewportColumns.find(c => c.idx === columnIdx);
  const ariaRowIndex = headerRowsCount + rowIdx + 1;
  const setIsShowMenu = useCallback((v) => {
    setContextMenuVisible(v)
    setShowMenuRowIdx?.(rowIdx, v);
  }, [setContextMenuVisible, setShowMenuRowIdx, rowIdx])
  const handleResetContext = () =>{
    setContextInfo(defaultContextInfo);
  }
  /**
 * 这边临时处理一下销毁的函数调用
 */
  const _innerRef = useRef<(v: any) => void>();
  _innerRef.current = setIsShowMenu;
  
  useEffect(() => {
    return () => {
      _innerRef.current?.(false)
    }
  }, [])
  useEffect(() => {
    function hideContextMenu() {
      if (contextMenuVisible) {
        setIsShowMenu(false);
        handleResetContext();
      }
    }
    function _contextMenu(event: Event) {
      if ((event.target as HTMLElement)?.closest?.('.rdg-row') !== document.querySelector(`[aria-rowindex="${ariaRowIndex}"]`)) {
        setIsShowMenu(false);
      }
    }
    window.addEventListener('click', hideContextMenu);
    if (contextMenuVisible) {
      window.addEventListener('contextmenu', _contextMenu);
    }
    return () => {
      window.removeEventListener('click', hideContextMenu);
      window.removeEventListener('contextmenu', _contextMenu);
    }
  }, [contextMenuVisible, setIsShowMenu])

  function onRowSelectionChange(rowIdx: number, isShiftClick: boolean, isCtrlClick: boolean) {
    onGridSelect({ rowIdx, columnIdx: -1, isShiftClick, isCtrlClick });
  }

  function handleContextMenu(event: React.MouseEvent<HTMLDivElement>) {
    event.preventDefault();
    event.stopPropagation();
    let x: number = 0, y: number = 0;
    const target = event.target as HTMLDivElement;
    function findNode(node: HTMLElement | null): any {
      if (!node) {
        return;
      } else if (['columnheader', 'gridcell'].includes(node.getAttribute('role') as string)) {
        const cellRect = node.parentElement?.getBoundingClientRect();
        const gridRect = (lazyStore.get('gridRef').current as HTMLDivElement)?.getBoundingClientRect();
        const contextRect = contextRef.current?.getBoundingClientRect() as DOMRect;
        const gridRight = gridRect?.right;
        const maxTop = gridRect?.height - contextRect?.height + scrollTop - 12;
        if (cellRect) {
          x = event.pageX - cellRect.left;
          if (event.pageX + 130 > gridRight) {
            x = x - 130;
          }
          y = Math.min(event.pageY - gridRect?.top + scrollTop, maxTop);
        }
        return {
          columnIdx: parseInt(node.getAttribute('aria-colindex') as string) - 1,
          rowIdx: parseInt(node.getAttribute('aria-rowindex') as string)
        }
      } else if (node.getAttribute('role') === 'row') {
        return;
      }
      return findNode(node.parentElement);
    }
    const { columnIdx, rowIdx } = findNode(target) ?? {};
    const column = viewportColumns.find(c => c.idx === columnIdx);
    const rowKey = rowKeyGetter(rows?.[rowIdx] as Record<string, any>);
    if (isNil(columnIdx) || !column) {
      return;
    }
    
    const isRowSelected = column.key === SELECT_COLUMN_KEY;
    if(isRowSelected){
      if (!selectedRows.has(rowKey)) {
        onRowSelectionChange(rowIdx, event.nativeEvent.shiftKey, isCtrlDown(event));
      }
    }else{
      if (!selectedColumns.has(column.key) && !selectedRows.has(rowKey) && !isCellInRange(columnIdx, rowIdx, selectedRange)) {
        onGridSelect({ columnIdx: column.idx, rowIdx });
      }
    }
    if (!contextMenuVisible) {
      setIsShowMenu(true);
    }
    if (x || y) {
      setContextInfo({
        x,
        y,
        columnIdx,
        rowIdx,
        column
      })
    }
    return false;
  }
  const isShowContext = contextMenuVisible && ContextMenuRender;

  return {
    contextMenuElement: (
      column ? 
      <div
        className={cellContext}
        ref={contextRef}
        style={{
          position: 'absolute',
          left: contextInfo?.x,
          top: contextInfo?.y,
          zIndex: isShowContext ? 4 : -1,
          opacity: isShowContext ? 1 : 0,
        }}
      >
        <ContextMenuRender
          row={row}
          rowKeyName={rowKeyName}
          columnKey={column!.key}
          selectedRange={selectedRange}
          contextMenuVisible={contextMenuVisible}
          setContextMenuVisible={() => {
            setIsShowMenu(false)
          }}
          config={getContextMenuConfig?.(row, column!)}
          isRowSelected={!!selectedRows.size}
          isColumnSelected={!!selectedColumns.size}
          rowIdx={rowIdx}
        />
      </div>
      : null
    ),
    handleContextMenu,
    handleResetContext
  }
}